/**
 * Ask AI on the reading page: chat with this module's book.
 *
 * Each question goes with where the student is reading (the chapter and the
 * sections on screen) and, when they selected a passage, that passage — so
 * "what does this mean?" is about what they are looking at. Selecting text in
 * the book puts it here, ready to ask about. Answers cite
 * the sections they came from; a click scrolls the book there.
 *
 * The mentor (a batch's mentor, a 1:1 student's mentor) gets a Students tab:
 * each student's conversation, read only.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Box, Tab, Tabs } from "@mui/material";

import { isBookSource } from "../Tutor/TutorChat";
import TutorTabs from "../Tutor/TutorTabs";
import TutorReview from "../Tutor/TutorReview";
import { FONT } from "../Sessions/sessionParts";
import {
  askBook, askBookThread, clearBook, createBookThread, getBookHistory, getBookJob, getBookStatus, getBookStudents,
  getBookThreadHistory, getBookThreads, getStudentBookHistory, getStudentBookThreadHistory, getStudentBookThreads,
  updateBookThread,
} from "./booksApi";

export default function BookTutor({ moduleId, place, quote, onClearQuote, onSource }) {
  const [tab, setTab] = useState("ai");
  const [canReview, setCanReview] = useState(false);

  const api = useMemo(() => ({
    ask: (body) => askBook(moduleId, body),
    job: (token) => getBookJob(moduleId, token),
    history: (before) => getBookHistory(moduleId, before),
    clear: () => clearBook(moduleId),
    status: async () => {
      const s = await getBookStatus(moduleId);
      setCanReview(!!s.can_review);
      return s;
    },
    // Side conversations: tabs branched from a passage of an answer or of the book.
    threads: () => getBookThreads(moduleId),
    createThread: (body) => createBookThread(moduleId, body),
    updateThread: (id, patch) => updateBookThread(moduleId, id, patch),
    askThread: (id, body) => askBookThread(moduleId, id, body),
    threadHistory: (id, before) => getBookThreadHistory(moduleId, id, before),
  }), [moduleId]);

  // A new selection brings the Ask AI tab forward with the passage in it.
  useEffect(() => { if (quote?.text) setTab("ai"); }, [quote]);

  /** Where they are: the selected passage's chapter and section if any, else the sections on screen. */
  const context = () => {
    const here = place ? place() : null;
    const chapter = Number.isInteger(quote?.chapter) ? quote.chapter : here?.chapter;
    const visible = (here?.sections || []).filter((s) => s.chapter === chapter).map((s) => s.id).slice(0, 12);
    const payload = {
      ...(Number.isInteger(chapter) ? { chapter_index: chapter } : {}),
      ...(quote?.section ? { section: quote.section, highlight_section: quote.section }
        : visible[0] ? { section: visible[0] } : {}),
      ...(visible.length ? { visible_sections: visible } : {}),
    };
    return { payload, bubble: {} };
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {canReview && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth"
          sx={{ minHeight: 40, borderBottom: "1px solid #eef0f2",
            "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontFamily: FONT, fontSize: 13 } }}>
          <Tab value="ai" label="Ask AI" />
          <Tab value="students" label="Students' questions" />
        </Tabs>
      )}
      <Box sx={{ flex: 1, minHeight: 0, display: tab === "ai" ? "block" : "none" }}>
        <TutorTabs api={api} pendingId={`book-${moduleId}`} context={context} onSource={onSource}
          isClickable={isBookSource} quote={quote} height="100%" onClearQuote={onClearQuote}
          labels={{
            online: "Ask about this module's reading",
            notReady: "The tutor hasn't been prepared for this reading yet",
            empty: "Ask anything about this reading — or select a passage in the book to ask about it.",
            footer: "Select text in the book or in an answer to ask about it. Your mentor can see this conversation.",
            clearBody: "The tutor starts fresh for this reading, and its tabs close with it. Your mentor can still see what was asked.",
          }} />
      </Box>
      {canReview && tab === "students" && (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <TutorReview key={moduleId} onSource={onSource} isClickable={isBookSource}
            loadStudents={() => getBookStudents(moduleId)}
            loadChat={(userId, before) => getStudentBookHistory(moduleId, userId, before)}
            loadThreads={(userId) => getStudentBookThreads(moduleId, userId)}
            loadThreadChat={(userId, threadId, before) => getStudentBookThreadHistory(moduleId, userId, threadId, before)} />
        </Box>
      )}
    </Box>
  );
}
